<template>
  <div class="overlay overlay--grow" v-if="modalVisible"> 
    <transition name="slideDown"> 

      <div>
        <div ref="target" class="overlay__inner" v-show="modalVisible" @click.stop :ref="modalRef">
          <div class="inner__paywall-overlay" v-if="!allowConfigModal">
            <div class="paywall-overlay__inner">
              <a href="/account/subscription">Subscription</a> required for this utility:<br>
              {{ currentUtility.name }}
            </div>
          </div>

          <div 
            class="pricing-row-config" 
            v-bind:class="{ 'pricing-row-config--disabled': !allowConfigModal }"
            :ref="scrollableRef"
          >
            <button class="btn btn-icon pricing-row-config__close" aria-label="close"
              @click.prevent="toggleModal"
            ><i class="fa fa-close"></i></button>
            <h3 class="pricing-row-config__head pricing-row-config__head--main"
              @mouseenter="moveable.draggable = true"
              @mouseleave="moveable.draggable = false"
            >Price Request Settings</h3>
            <div class="pricing-row-config__header">
              <button class="btn pricing-row-config__save" 
                @click.prevent="saveRowConfig"
                :disabled="saveButtonIsDisabled"
              >Save</button> 

              <div class="pricing-row-config__line" v-if="!editingName">
                <b class="line__label">Name <small>(<a href="#" @click.prevent="toggleNameEdit">Edit</a>)</small>:</b> 
                <template v-if="label">{{ label }}</template> <template v-else>-Not Defined-</template>
              </div>
              <div class="pricing-row-config__line" v-if="editingName">
                <b class="line__label">Name:</b> <input type="text" class="inline" v-model="localProfileName" @input="limitNameLength" ref="profile-name-input" placeholder="Optional Price Request Name">
                <button class="btn btn-tertiary btn-sm btn-icon" @click.prevent="toggleNameEdit">Update</button>
              </div>
              <div class="pricing-row-config__line">
                <b class="line__label">Profile:</b> <template v-if="facilityProfile">{{ facilityProfile }}</template> <template v-else>-Not Defined-</template>
              </div>
              <div class="pricing-row-config__line">
                <b class="line__label">Row #:</b> {{ ordinal }}
              </div>
            </div>

            <div class="pricing-row-config__primary-component">
              <h5 class="pricing-row-config__head">Price component to be displayed in collapsed view for each term:</h5>
              <label v-for="(pricingComponent, index) in pricingComponents">
                <input type="radio" 
                  :name="optionSetId"
                  :value="pricingComponent"
                  :id="'option' + uuid + index"
                  :checked="optionChecked(pricingComponent.slug)"
                  @change="updatePrimaryPricingComponent(pricingComponent)"
                > {{ pricingComponent.name }}
              </label>
              <label>
                <input type="radio"                   
                  :id="'option' + uuid + '-total'"
                  :name="optionSetId"
                  :value='{"slug":"total","name":"Total"}'
                  :checked="optionChecked('total')"
                  @change='updatePrimaryPricingComponent({"slug":"total","name":"Total"})'
                > Total
              </label>
            </div>

            <div class="pricing-row-config__existing-price">
              <label class="pricing-row-config__head">Enter an existing Supply price ($/kWh) to display on chart:</label>
              $0.&nbsp;<input 
                type="text" 
                ref="existing-supply-price"
                style="display: inline-block; width: 50px; margin-bottom: 3px;"
                :value="localExistingSupplyPrice"
                @keyup="updateExistingSupplyPrice"
              >
            </div>

            <div class="pricing-row-config__table">
              <h5 class="pricing-row-config__head">Set Price Alerts</h5>
              <div class="pricing-table pricing-table--outer-border">
                <table cellpadding="0" cellspacing="0" class="pricing-table__table">
                  <thead class="pricing-table__table-head">
                    <tr class="pricing-table__row">
                      <th class="pricing-table__cell pricing-table__cell--head pricing-table__cell--xsm">&nbsp;</th>
                      <th class="pricing-table__cell pricing-table__cell--head pricing-table__cell--sm">Term</th>
                      <th class="pricing-table__cell pricing-table__cell--head pricing-table__cell--smed">Component</th>
                      <th class="pricing-table__cell pricing-table__cell--head pricing-table__cell--sm">Trigger</th>
                      <th class="pricing-table__cell pricing-table__cell--head pricing-table__cell--sm">Price<br><small>($/kWh)</small></th>
                      <th class="pricing-table__cell pricing-table__cell--head pricing-table__cell--sm">
                        {{ new Intl.DateTimeFormat('en-US').format(new Date) }} Price<br><small>($/kWh)</small>
                      </th>
                      <th class="pricing-table__cell pricing-table__cell--head pricing-table__cell--xsm">&nbsp;</th>
                    </tr>
                  </thead>
                  <tbody class="pricing-table__table-body">
                    <template v-if="localPricingAlerts.length">
                      <pricing-alert-row 
                        v-for="(pricingAlert, index) in localPricingAlerts"
                        :key="index"
                        :index="index"
                        :id="pricingAlert.id"
                        :modal-visible="modalVisible"
                        :ordinal="ordinal"
                        :pricing-data="pricingData"
                        :pricing-components="pricingComponents"
                        :primary-pricing-component="primaryPricingComponent"
                        :pricing-alerts="localPricingAlerts"
                        :pricing-request="pricingRequest"
                        :pricing-request-id="pricingRequestId"
                        :term-length="termLength"
                        :term="pricingAlert.term"
                        :component="pricingAlert.pricing_component_id"
                        :trigger="pricingAlert.trigger"
                        :trigger-price="pricingAlert.price"
                        @removePricingAlert="removePricingAlert"
                        @setTriggerPrice="setPricingAlertTriggerPrice"
                        @setTerm="setPricingAlertTerm"
                        @setComponent="setPricingAlertComponent"
                        @setTrigger="setPricingAlertTrigger"
                      ></pricing-alert-row>
                    </template>
                    <template v-if="!localPricingAlerts.length">
                      <tr class="pricing-table__row">
                        <td colspan="7">
                          <i class="sz-k" v-if="allowPricingAlerts">No saved pricing alerts</i>
                          <i class="sz-k" v-if="!allowPricingAlerts">Creating and saving Alerts requires pricing output to first be generated (in Workspace).</i>
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </div>
            <button class="btn btn-tertiary pricing-row-config__btn-add-alert"
              v-if="allowPricingAlerts"
              @click="addPricingAlert"
            ><i class="fa fa-plus"></i>Add Alert</button>

            <template v-if="canAddTimeBasedAlerts">
              <div class="pricing-row-config__table">
                <h5 class="pricing-row-config__head">Set Time-Based Alerts</h5>
                <div class="pricing-table pricing-table--outer-border">
                  <table cellpadding="0" cellspacing="0" class="pricing-table__table">
                    <thead class="pricing-table__table-head">
                      <tr class="pricing-table__row">
                        <th class="pricing-table__cell pricing-table__cell--head pricing-table__cell--xsm">&nbsp;</th>
                        <th class="pricing-table__cell pricing-table__cell--head pricing-table__cell--smed">Time Unit</th>
                        <th class="pricing-table__cell pricing-table__cell--head pricing-table__cell--lg">Lead Time</th>
                        <th class="pricing-table__cell pricing-table__cell--head pricing-table__cell--smed">Alert Date</th>
                        <th class="pricing-table__cell pricing-table__cell--head pricing-table__cell--xsm">&nbsp;</th>
                      </tr>
                    </thead>
                    <tbody class="pricing-table__table-body">
                      <template v-if="localTimeBasedAlerts.length">
                        <time-based-alert-row 
                          v-for="(timeBasedAlert, index) in localTimeBasedAlerts"
                          :key="index"
                          :index="index"
                          :id="timeBasedAlert.id"
                          :modal-visible="modalVisible"
                          :ordinal="ordinal"
                          :pricing-request="pricingRequest"
                          :pricing-request-id="pricingRequestId"
                          :timeUnit="timeBasedAlert.timeUnit"
                          :leadTime="timeBasedAlert.leadTime"
                          @removeTimeBasedAlert="removeTimeBasedAlert"
                          @setTimeUnit="setTimeBasedAlertTimeUnit"
                          @setLeadTime="setTimeBasedAlertLeadTime"
                        ></time-based-alert-row>
                      </template>
                      <template v-if="!localTimeBasedAlerts.length">
                        <tr class="pricing-table__row">
                          <td colspan="7">
                            <i class="sz-k" v-if="allowTimeBasedAlerts">No saved time-based alerts</i>
                            <i class="sz-k" v-if="!allowTimeBasedAlerts">Creating and saving Alerts requires pricing output to first be generated (in Workspace).</i>
                          </td>
                        </tr>
                      </template>
                    </tbody>
                  </table>
                </div>
              </div>
              <button class="btn btn-tertiary pricing-row-config__btn-add-alert"
                v-if="allowTimeBasedAlerts"
                @click="addTimeBasedAlert"
              ><i class="fa fa-plus"></i>Add Alert</button>
            </template>

          </div>
        </div>

        <Moveable
          className="moveable"
          :target="$refs.target"
          :draggable="moveable.draggable"
          :origin="false"
          @drag="handleDrag"
        />
      </div>

    </transition>
  </div>
</template>

<script>
import { nextTick } from 'vue'
import axios from 'axios'
import Moveable from 'vue3-moveable'
import swal from 'sweetalert'
import moment from 'moment-timezone'
import PricingAlertRow from '../common/PricingAlertRow.vue'
import TimeBasedAlertRow from '../common/TimeBasedAlertRow.vue'

let uuid = 0

export default {
  props: ['allowConfigModal', 'modalVisible', 'ordinal', 'label', 'facilityProfile', 'pricingData', 'pricingComponents', 'primaryPricingComponent', 'existingSupplyPrice', 'pricingAlerts', 'timeBasedAlerts', 'pricingRequest', 'pricingRequestId', 'termLength'],
  components: {
    'pricing-alert-row': PricingAlertRow,
    'time-based-alert-row': TimeBasedAlertRow,
    Moveable,
  },
  data() {
    return {
      optionSetId: 'option-set-' + uuid,
      modalRef: 'row-config-modal-' + uuid,
      scrollableRef: 'row-config-scroll-' + uuid,
      maxTriggerPriceDecimalPlaces: 4,
      localPricingAlerts: [],
      localTimeBasedAlerts: [],
      localPrimaryPricingComponent: '',
      localExistingSupplyPrice: '',
      localProfileName: '',
      editingName: false,
      changesMade: false,
      isProcessing: false,
      maxNameLength: 50,
      moveable: {
        draggable: false,
      },
    }
  },
  beforeCreate() {
    uuid += 1;
  },
  computed: {
    uuid() {
      return uuid
    },
    allowPricingAlerts() {
      if (Object.keys(this.pricingData).length) {
        return true
      }
      return false
    },
    allowTimeBasedAlerts() {
      if (Object.keys(this.pricingData).length) {
        return true
      }
      return false
    },
    timezone() {
      return this.$store.getters.userTimezone
    },
    currentDate() {
      let utcDate = moment().utc()

      if (this.timezone) {
        return moment(utcDate).tz(this.timezone)
      }

      return moment(utcDate).local()
    },
    currentUtility() {
      return this.$store.getters.dashboardSortUtility
    },
    saveButtonIsDisabled() {
      return this.editingName || this.isProcessing
    },
    canAddTimeBasedAlerts() {
      return moment(this.pricingRequest.inputs.startMonth) > this.currentDate.add(1, 'days')
    }
  },
  methods: {
    handleDrag({ target, transform }) {
      let ogTransform = target.style.transform

      target.style.transform = transform

      let bounding = target.getBoundingClientRect();

      if (bounding.top < 0) {
        target.style.transform = ogTransform
      }
    },
    toggleModal() {
      if (!this.changesMade || !this.allowConfigModal) {
        this.$emit('toggleModal')
        return
      }

      let swalContent = document.createElement('div');
      swalContent.innerHTML = '<p class="alerts message alerts--no-brdr alerts--close-btn">This action will clear any unsaved changes.<br>Do you want to save changes?</p>';

      swal({
        content: swalContent,
        closeOnEsc: false,
        closeOnClickOutside: false,
        buttons: {
          cancel: {
            text: "Don't Save",
            value: null,
            visible: true,
            className: "",
            closeModal: true,
          },
          confirm: {
            text: "Save",
            value: true,
            visible: true,
            className: "",
            closeModal: true
          }
        }
      }).then((result) => {

        if (result) {
          this.saveRowConfig()
        } else {
          this.$emit('toggleModal')
        }

      }).catch(function(action) {
        if (action === 'close') {
          return
        } 
      })
    },
    optionChecked(slug) {
      if (this.localPrimaryPricingComponent && slug === this.localPrimaryPricingComponent.slug) {
        return true
      }

      return false
    },
    updatePrimaryPricingComponent(pricingComponent) {
      this.localPrimaryPricingComponent = pricingComponent
      this.changesMade = true
    },
    updateExistingSupplyPrice(event) {
      let val = event.target.value.replace(/\D/g,'')

      this.$refs['existing-supply-price'].value = val

      this.localExistingSupplyPrice = val
      this.changesMade = true
    },
    addPricingAlert() {
      if (! this.allowConfigModal) return false

      this.localPricingAlerts.push({
        term: '',
        component: '',
        trigger: '',
        price: '',
      })

      this.changesMade = true

      nextTick(() => {
        let scrollableHeight = this.$refs[this.scrollableRef].clientHeight

        if (this.$refs[this.modalRef]) {
          this.$refs[this.modalRef].scrollTo({ top: scrollableHeight, behavior: 'smooth' })
        }
      })
    },
    removePricingAlert(index) {
      if (! this.allowConfigModal) return false

      let targetId = this.localPricingAlerts[index].id

      let swalContent = document.createElement('div');
      swalContent.innerHTML = '<p class="alerts message alerts--no-brdr alerts--close-btn">Are you sure you want to delete this price alert?</p>';

      swal({
        content: swalContent,
        closeOnEsc: false,
        closeOnClickOutside: false,
        buttons: {
          cancel: {
            text: "Cancel",
            value: null,
            visible: true,
            className: "",
            closeModal: true,
          },
          confirm: {
            text: "Yes",
            value: true,
            visible: true,
            className: "",
            closeModal: true
          }
        }
      }).then(result => {
        if (result) {
          if (! targetId) {
            this.localPricingAlerts.splice(index, 1)

          } else {
            axios.delete('/pricing-calculator/me/pricing-alerts/' + targetId)
              .then(response => {
                this.localPricingAlerts.splice(index, 1)
                this.$emit('waitForUpdatedRequests')
                this.$store.dispatch('fetchSavedPricingRequests')

                setTimeout(() => {
                  this.populateLocalProperties()
                }, 1000)
              })
          }
        }
      })
    },
    addTimeBasedAlert() {
      if (! this.allowConfigModal) return false

      this.localTimeBasedAlerts.push({
        timeUnit: 'days',
        leadTime: null
      })

      this.changesMade = true

      nextTick(() => {
        let scrollableHeight = this.$refs[this.scrollableRef].clientHeight

        if (this.$refs[this.modalRef]) {
          this.$refs[this.modalRef].scrollTo({ top: scrollableHeight, behavior: 'smooth' })
        }
      })
    },
    removeTimeBasedAlert(index) {
      if (! this.allowConfigModal) return false

      let targetId = this.localTimeBasedAlerts[index].id

      let swalContent = document.createElement('div');
      swalContent.innerHTML = '<p class="alerts message alerts--no-brdr alerts--close-btn">Are you sure you want to delete this time-based alert?</p>';

      swal({
        content: swalContent,
        closeOnEsc: false,
        closeOnClickOutside: false,
        buttons: {
          cancel: {
            text: "Cancel",
            value: null,
            visible: true,
            className: "",
            closeModal: true,
          },
          confirm: {
            text: "Yes",
            value: true,
            visible: true,
            className: "",
            closeModal: true
          }
        }
      }).then(result => {
        if (result) {
          if (! targetId) {
            this.localTimeBasedAlerts.splice(index, 1)

          } else {
            axios.delete('/pricing-calculator/me/time-based-alerts/' + targetId)
              .then(response => {
                this.localTimeBasedAlerts.splice(index, 1)
                this.$emit('waitForUpdatedRequests')
                this.$store.dispatch('fetchSavedPricingRequests')

                setTimeout(() => {
                  this.populateLocalProperties()
                }, 1000)
              })
          }
        }
      })
    },
    setPricingAlertTriggerPrice(index, val) {
      this.localPricingAlerts[index].price = val
      this.changesMade = true
    },
    setPricingAlertTerm(index, term) {
      this.localPricingAlerts[index].term = term
      this.changesMade = true
    },
    setPricingAlertComponent(index, component) {
      this.localPricingAlerts[index].pricing_component_id = component
      this.changesMade = true
    },
    setPricingAlertTrigger(index, trigger) {
      this.localPricingAlerts[index].trigger = trigger
      this.changesMade = true
    },
    setTimeBasedAlertTimeUnit(index, timeUnit) {
      this.localTimeBasedAlerts[index].timeUnit = timeUnit
      this.changesMade = true
    },
    setTimeBasedAlertLeadTime(index, leadTime) {
      this.localTimeBasedAlerts[index].leadTime = leadTime
      this.changesMade = true
    },
    async saveRowConfig() {
      if (! this.allowConfigModal) return false

      this.isProcessing = true

      let alert
      let pricingAlertsPayload = {alerts: []}
      let timeBasedAlertsPayload = {alerts: []}

      // Pricing Alerts
      for (let i = 0; i < this.localPricingAlerts.length; ++i) {
        alert = {
          term: this.localPricingAlerts[i].term,
          trigger: this.localPricingAlerts[i].trigger.toLowerCase(),
          price: (('0.' + this.localPricingAlerts[i].price.replace('0.', '')) * 1)
        }

        if (this.localPricingAlerts[i].pricing_component_id) {
          // Add the ID for any component except total.
          alert.pricing_component_id = this.localPricingAlerts[i].pricing_component_id
        }

        if (this.localPricingAlerts[i].id) {
          // We're updating an alert, which requires an alert id.
          alert.id = this.localPricingAlerts[i].id
          pricingAlertsPayload.alerts.push(alert)

        } else {
          // We're adding an alert, which requires a pricing request id.
          alert.user_inputs_id = this.pricingRequestId
          pricingAlertsPayload.alerts.push(alert)
        }
      }

      // Time-Based Alerts
      for (let i = 0; i < this.localTimeBasedAlerts.length; ++i) {
        alert = {
          time_unit: this.localTimeBasedAlerts[i].timeUnit,
          lead_time: this.localTimeBasedAlerts[i].leadTime
        }

        if (this.localTimeBasedAlerts[i].id) {
          // We're updating an alert, which requires an alert id.
          alert.id = this.localTimeBasedAlerts[i].id
          timeBasedAlertsPayload.alerts.push(alert)

        } else {
          // We're adding an alert, which requires a pricing request id.
          alert.user_inputs_id = this.pricingRequestId
          timeBasedAlertsPayload.alerts.push(alert)
        }
      }

      // Upsert pricing alerts.
      if (pricingAlertsPayload.alerts.length) {
        try {
          await axios.put('/pricing-calculator/me/pricing-alerts', pricingAlertsPayload)
        } catch (error) {
          swal('', 'An unexpected error occurred while saving the pricing alerts. Please ensure all fields are completed.', 'error')
          return
        }
      }

      // Upsert time-based alerts.
      if (timeBasedAlertsPayload.alerts.length) {
        try {
          await axios.put('/pricing-calculator/me/time-based-alerts', timeBasedAlertsPayload)
        } catch (error) {
          swal('', 'An unexpected error occurred while saving the time-based alerts. Please ensure all fields are completed.', 'error')
          return
        }
      }

      // Save the selected price component, as well as the existing supply price.
      this.$store.dispatch('updatePricingRequestPrimaryComponent', {
        id: this.pricingRequestId,
        primaryPricingComponent: this.localPrimaryPricingComponent,

        // Convert to float, since that's the actual value (it's only inputted as an integer).
        existingSupplyPrice: (('0.' + this.localExistingSupplyPrice.toString().replace('0.', '')) * 1),
      })

      // We need to wait for a half a second to give the server cache time to 
      // forget the old pricing component data, as the `toggleModal` event
      // below will trigger the refetching of all price request data.
      setTimeout(() => {
        this.$emit('toggleModal')
      }, 500)
    },
    populateLocalProperties() {
      this.localPricingAlerts = []
      this.localTimeBasedAlerts = []

      // Convert to an integer since that's how it's inputted.
      if (this.existingSupplyPrice) {
        this.localExistingSupplyPrice = this.existingSupplyPrice.toString().replace('0.', '')
      }

      this.localPrimaryPricingComponent = this.primaryPricingComponent ? {
        children: this.primaryPricingComponent.children,
        data_type_ids: this.primaryPricingComponent.data_type_ids,
        description: this.primaryPricingComponent.description,
        id: this.primaryPricingComponent.id,
        limit_eb_access: this.primaryPricingComponent.limit_eb_access,
        name: this.primaryPricingComponent.name,
        parent: this.primaryPricingComponent.parent,
        slug: this.primaryPricingComponent.slug
      } : ''

      if (this.pricingAlerts) {
        let alerts = _.cloneDeep(this.pricingAlerts)
        for (let i = 0; i < alerts.length; ++i) {
          this.localPricingAlerts.push(alerts[i])
        }
      }

      if (this.timeBasedAlerts) {
        let alerts = _.cloneDeep(this.timeBasedAlerts)
        for (let i = 0; i < alerts.length; ++i) {
          this.localTimeBasedAlerts.push({
            ...alerts[i], 
            ...{
              timeUnit: alerts[i].time_unit,
              leadTime: alerts[i].lead_time,
            }
          })
        }
      }
    },
    toggleNameEdit() {
      if (!this.allowConfigModal) return false

      if (!this.localProfileName) {
        this.localProfileName = this.label 
      }

      this.editingName = !this.editingName

      nextTick(() => {
        if (this.editingName) {
         this.$refs['profile-name-input'].focus()

        } else {
          let obj = {
            id: this.pricingRequestId,
            name: this.localProfileName
          }
          this.$emit('waitForUpdatedRequests')
          this.$store.dispatch('updatePricingRequestName', obj)
        }
      })
    },
    limitNameLength() {
      if (this.localProfileName.length > this.maxNameLength) {
        this.localProfileName = this.localProfileName.slice(0, this.maxNameLength)
      }
    }
  },
  created() {
    this.populateLocalProperties()
  },
  watch: {
    modalVisible(bool) {
      if (bool) {
        if (this.$refs[this.modalRef]) {
          this.$refs[this.modalRef].scrollTo({ top: 0, behavior: 'smooth' })
        }

        this.populateLocalProperties()
      }
    }
  }
}
</script>
